import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AuthError, claimAccountOpen, forgotPassword } from '../api/auth'
import { fetchContent, has, text } from '../api/content'
import AuthLayout, { Field, FormError, SubmitButton } from './AuthLayout'

/**
 * Asks the studio for a reset link.
 *
 * **The confirmation is deliberately non-committal.** The server answers the
 * same whether or not the address has an account, so this screen must not say
 * "we found you" or "we didn't" — either would turn the form into a list of who
 * is registered, one address at a time.
 *
 * That constraint is why the "nothing arrived?" advice sits beside the success
 * message rather than behind a link. The copy has to serve a person who typed
 * their address correctly *and* one who typed the wrong one, because the screen
 * genuinely cannot tell them apart.
 */
export default function ForgotPasswordView() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)

  // The studio's number, for the same reason as the claim screen: this page
  // sits outside the public shell, which is where it otherwise appears.
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  // The same door as the sign-in page's, asked the same way (D-614).
  const { data: claimOpen } = useQuery({
    queryKey: ['claim-account-available'],
    queryFn: claimAccountOpen,
    staleTime: Infinity,
  })

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!email.trim()) return

    setBusy(true)
    setError(null)

    try {
      await forgotPassword(email.trim())
      setSent(true)
    } catch (e) {
      // The only reachable failure is the request itself — the server returns
      // OK whatever it found.
      setError(
        e instanceof AuthError
          ? e
          : new AuthError('Could not reach the studio system. Check your connection.', 0),
      )
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`If ${email.trim()} has an account, a link is on its way. It is good for 2 hours and can only be used once.`}
      >
        <p className="text-[13px] leading-relaxed text-ink-3">
          Nothing arrived? Check the junk folder first. If it is still not there, the studio
          may have a different address on file
          {has(content, 'studio.phone') ? (
            <>
              {' '}&mdash; ring the studio on{' '}
              <a
                href={`tel:${text(content, 'studio.phone').replace(/[^\d+]/g, '')}`}
                className="font-semibold text-accent"
              >
                {text(content, 'studio.phone')}
              </a>{' '}
              and they can put it right.
            </>
          ) : (
            <> &mdash; ring the studio and they can put it right.</>
          )}
        </p>

        <p className="mt-5 text-center text-[13px] text-ink-3">
          <Link to="/signin" className="font-semibold text-accent">
            Back to sign in
          </Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the address you sign in with and we will send you a link."
    >
      {error && <FormError title={error.message} detail={error.detail} />}

      <form onSubmit={submit} noValidate>
        <Field
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          required
        />

        <SubmitButton busy={busy}>Send me a link</SubmitButton>
      </form>

      {claimOpen === true && (
        <p className="mt-5 text-center text-[13px] text-ink-3">
          Never set up an account?{' '}
          <Link to="/claim-account" className="font-semibold text-accent">
            Start here
          </Link>
        </p>
      )}
    </AuthLayout>
  )
}
